import { cloneDeep } from 'lodash'
import { ToastProgrammatic as Toast } from 'buefy'
import { snakeCaseKeys } from '~/mixins/objects'

const defaultState = {
  athlete: null,
  user: null,
  submitting: false,
  error: null,
  editingSection: null
}

export const state = () => cloneDeep(defaultState)

export const getters = {
  athlete(state) {
    return state.athlete
  },
  user(state) {
    return state.user
  },
  submitting(state) {
    return state.submitting
  },
  error(state) {
    return state.error
  },
  editingSection(state) {
    return state.editingSection
  }
}

export const mutations = {
  athlete(state, athlete) {
    state.athlete = athlete
  },
  user(state, user) {
    state.user = user
  },
  submitting(state, submitting) {
    state.submitting = submitting
  },
  error(state, error) {
    state.error = error
  },
  editingSection(state, editingSection) {
    state.editingSection = editingSection
  }
}

export const actions = {
  async search({ commit }, searchValue) {
    let requestSearchParam

    if (typeof searchValue === 'string' && searchValue.includes('@')) {
      requestSearchParam = { email: searchValue }
    } else {
      requestSearchParam = { athlete_id: searchValue }
    }

    commit('submitting', true)

    await this.$coreApi
      .$get('staff/iam/search', { params: requestSearchParam })
      .then(response => {
        commit('error', null)

        if (response.data.type === 'users') {
          commit('user', response.data)
          commit('athlete', response.data.athlete)
        }

        if (response.data.type === 'accountManagementAthletes') {
          commit('user', null)
          commit('athlete', response.data)
        }
      })
      .catch(error => {
        commit('user', null)
        commit('athlete', null)
        commit('error', error.response)
      })
      .finally(() => {
        commit('submitting', false)
      })
  },
  async changePersonalInformation({ commit, dispatch, state }, form) {
    commit('submitting', true)

    await this.$coreApi
      .$post(`staff/athletes/${state.athlete.id}/change_personal_information`, {
        name: form.name,
        date_of_birth: form.dateOfBirth,
        gender: form.gender,
        nationality_id: form.nationality.id
      })
      .then(_ => {
        commit('error', null)
        console.log('success')
        Toast.open({
          duration: 7000,
          message: translations[this.$i18n.locale].changeInformationSuccessMessage,
          type: 'is-success'
        })
      })
      .catch(error => {
        console.log('error', error.response)
        commit('error', error.response)
      })
      .finally(() => {
        console.log('finally')
        commit('submitting', false)
        commit('editingSection', null)
      })

    // Refresh athlete information.
    await dispatch('search', state.athlete.id)
  },
  async changeAccountInformation({ commit, dispatch, state }, form) {
    commit('submitting', true)

    await this.$coreApi
      .$post('/staff/iam/change_data', {
        user_id: state.user.id,
        email: form.email,
        phone: form.phone,
        confirm_account: form.confirmAccount
      })
      .then(_ => {
        commit('error', null)
        // Rehidrate user e-mail in case of change, this is useful to trigger the entire user rehidration with the correct e-mail.
        commit('user', { ...state.user, email: form.email })

        Toast.open({
          duration: 7000,
          message: translations[this.$i18n.locale].changeInformationSuccessMessage,
          type: 'is-success'
        })
      })
      .catch(error => {
        commit('error', error.response)
      })
      .finally(() => {
        commit('submitting', false)
        commit('editingSection', null)
      })

    // Refresh user/athlete information.
    await dispatch('search', state.user.email)
  },
  async changeAthleteAddress({ commit, dispatch, state }, form) {
    commit('submitting', true)

    await this.$coreApi
      .$post(`staff/athletes/${state.athlete.id}/change_athlete_address`, {
        address: snakeCaseKeys(form)
      })
      .then(_ => {
        commit('error', null)

        Toast.open({
          duration: 7000,
          message: translations[this.$i18n.locale].changeInformationSuccessMessage,
          type: 'is-success'
        })
      })
      .catch(error => {
        commit('error', error.response)
      })
      .finally(() => {
        commit('submitting', false)
        commit('editingSection', null)
      })

    // Refresh user/athlete information.
    await dispatch('search', state.athlete.id)
  },
  async changePhoto({ commit, dispatch, state }, file) {
    commit('submitting', true)

    await this.$coreApi
      .$post(`staff/athletes/${state.athlete.id}/change_photo`, {
        photo_file: snakeCaseKeys(file)
      })
      .then(_ => {
        commit('error', null)

        Toast.open({
          duration: 7000,
          message: translations[this.$i18n.locale].photoChangedSuccessMessage,
          type: 'is-success'
        })
      })
      .catch(error => {
        commit('error', error.response)
      })
      .finally(() => {
        commit('submitting', false)
        commit('editingSection', null)
      })

    // Refresh user/athlete information.
    await dispatch('search', state.athlete.id)
  },
  async changeRankingPhoto({ commit, dispatch, state }, file) {
    commit('submitting', true)

    await this.$coreApi
      .$post(`staff/athletes/${state.athlete.id}/change_ranking_photo`, {
        photo_file: snakeCaseKeys(file)
      })
      .then(_ => {
        commit('error', null)

        Toast.open({
          duration: 7000,
          message: translations[this.$i18n.locale].photoChangedSuccessMessage,
          type: 'is-success'
        })
      })
      .catch(error => {
        commit('error', error.response)
      })
      .finally(() => {
        commit('submitting', false)
        commit('editingSection', null)
      })

    // Refresh user/athlete information.
    await dispatch('search', state.athlete.id)
  },
  async changeProvisionalPeriod({ commit, dispatch, state }, form) {
    await this.$coreApi
      .$post(`staff/athletes/${state.athlete.id}/change_provisional_period`, {
        provisional_period_starts_at: form.provisionalPeriodStartsAt,
        provisional_period_ends_at: form.provisionalPeriodEndsAt
      })
      .then(_ => {
        commit('error', null)

        Toast.open({
          duration: 7000,
          message: translations[this.$i18n.locale].provisionalPeriodChangedSuccessMessage,
          type: 'is-success'
        })
      })
      .catch(error => {
        commit('error', error.response)
      })
      .finally(() => {
        commit('submitting', false)
        commit('editingSection', null)
      })

    // Refresh user/athlete information.
    await dispatch('search', state.athlete.id)
  }
}

export const translations = {
  'en-US': {
    changeInformationSuccessMessage: 'Information successfully updated.',
    photoChangedSuccessMessage: 'Photo successfully updated.',
    provisionalPeriodChangedSuccessMessage: 'Provisional period successfully updated.'
  },
  'pt-BR': {
    changeInformationSuccessMessage: 'Informações atualizadas com sucesso.',
    photoChangedSuccessMessage: 'Foto atualizada com sucesso.',
    provisionalPeriodChangedSuccessMessage: 'Período provisório atualizado com sucesso.'
  }
}
